import { retrieveWebSpeakerUrl } from './urls';
import * as proto from './proto';
import Long from 'long';
import {
  CallbackMethod,
  CallbackType,
  LanguageCode,
  SmartErrorCode,
} from '../const/common';
import { loadToken } from 'utils/ls/token';
import { Buffer } from 'buffer';
import SpeechInputsMessage = proto.si.SpeechInputsMessage;
import ISpeechInputsMessage = proto.si.ISpeechInputsMessage;

export interface SpeakerConfig {
  srcLang: string;
  provider?: string;
}
export class SpeakerClient {
  ws!: WebSocket;
  conf: SpeakerConfig;
  voiceSeq: number;
  playbackTimeStart: number;
  playbackTimeEnd: number;
  sessionStart: number;
  terminalInfo: proto.live.ITerminalInfo;
  lastHeartbeatTime: number;
  runTimer: number | null;
  shouldReconnect: boolean;
  clientCallback: CallbackMethod;

  constructor(
    conf: SpeakerConfig,
    terminalInfo: proto.live.ITerminalInfo,
    clientCallback: CallbackMethod
  ) {
    this.conf = conf;
    this.terminalInfo = terminalInfo;
    this.lastHeartbeatTime = Date.now();
    this.voiceSeq = 0;
    this.playbackTimeStart = 0;
    this.playbackTimeEnd = 0;
    this.runTimer = null;
    this.sessionStart = 0;
    this.shouldReconnect = false;
    this.clientCallback = clientCallback;
    this.initWs();
  }

  checkSocketRestart() {
    if (this.runTimer) {
      clearInterval(this.runTimer);
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.runTimer = setInterval(() => {
      const isSocketOpened = this.ws.readyState === WebSocket.OPEN;
      if (isSocketOpened) {
        const initConfig: ISpeechInputsMessage = {
          eventType: SpeechInputsMessage.SpeechEventType.PING,
        };
        this.ws.send(SpeechInputsMessage.encode(initConfig).finish());
      }
      const nowTime = Date.now();
      if (this.shouldReconnect && nowTime - this.lastHeartbeatTime > 3000) {
        this.restartWs();
        this.clientCallback(CallbackType.FAIL, {
          code: SmartErrorCode.HEART_BEAT_ERROR,
          message: 'speaker heart beat timeout.',
        });
        this.lastHeartbeatTime = nowTime;
      }
    }, 1000);
  }

  sendConfig() {
    const {
      srcLang,
      provider = srcLang !== LanguageCode.JAPANESE
        ? 'lab'
        : 'microsoft_private',
    } = this.conf;
    const initConfig: ISpeechInputsMessage = {
      eventType: SpeechInputsMessage.SpeechEventType.CONFIGURATION,
      configuration: {
        recognition: {
          languageCode: srcLang,
          speechProvider: provider,
          speechContexts: [],
          timestampAlignment: true,
        },
        translation: {
          languageCodes: ['en', 'ja', 'zh'],
        },
      },
    };
    const req = SpeechInputsMessage.encode(initConfig).finish();
    this.ws.send(req);
  }

  sendVoiceBuffer(buf: ArrayBuffer, playbackTimestamp: number): void {
    if (!this.shouldReconnect || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }
    if (this.voiceSeq === 0) {
      this.playbackTimeStart = playbackTimestamp;
    }
    this.playbackTimeEnd = playbackTimestamp;
    const msg: ISpeechInputsMessage = {
      eventType: SpeechInputsMessage.SpeechEventType.AUDIO,
      audio: {
        sequence: Long.fromValue(this.voiceSeq),
        binary: Buffer.from(buf),
        ptsMs: Long.fromValue(playbackTimestamp),
        unixMs: Long.fromValue(Date.now()),
      },
    };
    const req = SpeechInputsMessage.encode(msg).finish();
    this.ws.send(req);
    this.voiceSeq++;
  }

  restartWs() {
    if (this.ws.readyState === WebSocket.CONNECTING) {
      return;
    }
    this.close();
    this.shouldReconnect = true;
    this.initWs();
  }

  resetConfig(conf: SpeakerConfig) {
    this.conf = conf;
  }

  initWs(): void {
    const { nid, cid, deviceId } = this.terminalInfo;
    const wsUrl = retrieveWebSpeakerUrl();
    const url = `${wsUrl}?token=${loadToken()}&nid=${nid}&cid=${cid}&device_id=${deviceId}`;
    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      this.sendConfig();
      this.voiceSeq = 0;
      this.shouldReconnect = true;
      this.sessionStart = Date.now();
      this.checkSocketRestart();
      this.clientCallback(CallbackType.OPEN);
    };

    this.ws.onclose = (event) => {
      const timeSeconds = (Date.now() - this.sessionStart) / 1000;
      const voiceSeconds = (this.voiceSeq * 2730 * 1000) / 16000 / 2 / 1000;
      const playbackSeconds =
        (this.playbackTimeEnd - this.playbackTimeStart) / 1000;
      this.clientCallback(CallbackType.CLOSE, {
        code: event.code,
        message: event.reason,
        extra: {
          log: `cid elapse # ${cid}: voice seq ${this.voiceSeq}, voice elapse ${voiceSeconds}, time elapse ${timeSeconds}, playback elapse ${playbackSeconds}`,
        },
      });
    };

    this.ws.onmessage = () => {
      this.lastHeartbeatTime = Date.now();
    };

    this.ws.onerror = (e) => {
      this.clientCallback(CallbackType.FAIL, {
        code: SmartErrorCode.SPEAKER_SOCKET_ERROR,
        message: e,
      });
    };
  }

  close() {
    this.ws.close();
  }

  stop() {
    this.shouldReconnect = false;
  }

  continue() {
    this.shouldReconnect = true;
  }
}
